import { yupResolver } from "@hookform/resolvers/yup";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { ResponsiveConfirmDialog } from "@/components/common/modals/responsiveConfirmDialog";
import { VIOLET } from "@/config/mui/colorPalette";
import useCreditCardInputs from "@/hooks/payments/useCreditCardInputs";
import AddCreditCardForm, { FormInput } from "./addCreditCardForm";

type CreditCardFormContainerProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onConfirm: (newCardOptions?: {
    zipCode: string;
    shouldSaveCard: boolean;
  }) => void;
  actionPending?: boolean;
  shouldAskForSavingCard?: boolean;
  defaultSaveCardAfterPayment?: boolean;
  buttonTitle?: string;
};

const getSchema = (shouldAskForSavingCard: boolean) =>
  yup.object({
    shouldSaveCard: shouldAskForSavingCard ? yup.boolean() : undefined,
    zipCode: yup
      .string()
      .test(
        "zipCode",
        "Please provide valid US zip code",
        (value: string) => !Number.isNaN(+value) && value.length === 5
      ),
  });

export default function CreditCardFormContainer({
  isOpen,
  setIsOpen,
  onConfirm,
  actionPending,
  shouldAskForSavingCard = true,
  defaultSaveCardAfterPayment,
  buttonTitle,
}: CreditCardFormContainerProps) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<FormInput>({
    resolver: yupResolver(getSchema(shouldAskForSavingCard)),
  });

  const {
    paymentCardValidationInfo,
    isPaymentCardInfoValid,
    showPaymentCardEmptyFieldErrors,
    setShowPaymentCardEmptyFieldErrors,
    onElementChange,
  } = useCreditCardInputs();

  const onSubmit = (data: FormInput) => {
    if (!isPaymentCardInfoValid || !!errors.zipCode) {
      setShowPaymentCardEmptyFieldErrors(true);
      return;
    }
    return onConfirm(data);
  };

  useEffect(() => {
    reset();
    setShowPaymentCardEmptyFieldErrors(false);
  }, [isOpen, reset, setShowPaymentCardEmptyFieldErrors]);

  const formContent = (
    <AddCreditCardForm
      control={control}
      errors={errors}
      paymentCardValidationInfo={paymentCardValidationInfo}
      showPaymentCardEmptyFieldErrors={showPaymentCardEmptyFieldErrors}
      defaultSaveCardAfterPayment={defaultSaveCardAfterPayment}
      onElementChange={onElementChange}
      shouldAskForSavingCard={shouldAskForSavingCard}
      sx={{
        "& .MuiInputBase-input": {
          padding: "11px 13px",
        },
      }}
    />
  );

  return (
    <ResponsiveConfirmDialog
      isOpen={isOpen}
      title="Enter card details"
      titleAlign="center"
      onClose={() => setIsOpen(false)}
      onSubmit={handleSubmit(onSubmit)}
      submitText={buttonTitle || "Finalize payment"}
      isConfirmDisabled={actionPending}
      modalProps={{
        sx: {
          "& button": {
            height: "40px !important",
            fontSize: "14px !important",
          },
        },
      }}
      drawerProps={{
        crossIconColor: VIOLET[90],
      }}
    >
      {formContent}
    </ResponsiveConfirmDialog>
  );
}
